import { addressToString } from '@aloft/toolkit-shared';
import { Flex, IconButton, SizeProperty, theme } from '@aloft/toolkit-sui';

import { useParams } from 'react-router';

import { LoadingScreen } from '../../components/LoadingScreen/LoadingScreen.tsx';
import { useGetListing } from '../../controller/hooks/listings/useGetListing.ts';
import { useActiveReferenceBarID } from '../../controller/hooks/useActiveReferenceBarID.ts';
import { selectBackupListingDataById } from '../../redux/features/selectors/propertyDataSelectors/backupListingDataSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { Tkv2DataComponent } from './Tkv2DataComponent.tsx';
import { useParsedActiveID } from './hooks/useParsedActiveID.ts';
import { isReferenceViewPropertyIDParsed } from './referenceViewID/isReferenceViewPropertyIDParsed.ts';
import { parseReferenceViewID } from './referenceViewID/parseReferenceViewID.ts';
import { ReferenceViewID } from './referenceViewID/types.ts';
import { DataSheet } from './types.ts';

import './Tkv2ReferenceView.scss';
import { getConfig } from '../../utils/getConfig.ts';
import { selectOrder } from '../../redux/features/selectors/orderSelectors.ts';

function getPropertyID(activeID: ReferenceViewID): string {
  const referenceViewID = parseReferenceViewID(activeID);
  if (isReferenceViewPropertyIDParsed(referenceViewID)) {
    return referenceViewID.type === 'subject' ? 'subject' : referenceViewID.compID;
  }

  return 'NO_PROPERTY_ID';
}

type Tkv2ReferenceViewModuleProps = {
  isPopout: boolean;
  isWrapped?: boolean;
  dataSheet: DataSheet | null;
  popOutIconPosition?: {
    top?: SizeProperty;
    right?: SizeProperty;
    bottom?: SizeProperty;
    left?: SizeProperty;
  };
};

const IconWrapper = ({
  isPopout,
  isWrapped,
  popOutIconPosition,
  dataSheet,
  activeID,
}: Tkv2ReferenceViewModuleProps & { activeID: ReferenceViewID }) => {
  const { orderId: orderID } = useParams();
  const order = useAppSelector(selectOrder);

  if (isPopout) {
    return (
      <Flex position="absolute" top="0" right="1rem" zIndex={theme.zIndices.base + 1}>
        <IconButton
          icon="close"
          variant="tertiary"
          onClick={window.close}
          ariaLabel="close window"
          iconSize="lg"
          size="md"
        />
      </Flex>
    );
  }

  if (isWrapped) return null;

  return (
    <Flex position="absolute" {...popOutIconPosition} zIndex={theme.zIndices.docked}>
      <div className="reference-view-external-link">
        <IconButton
          icon="external-link"
          variant="tertiary"
          onClick={() => {
            // Instead of opening an iframe, we open the web report directly in a new tab
            if (dataSheet === 'inspection-report') {
              const baseWebReportUrl = getConfig('INSPECTIFY_WEB_REPORT_BASE_URL');
              const reportId = order.inspectionData?.reportId;
              const orderNumber = order.orderNumber;

              if (!reportId || !orderNumber) {
                return;
              }

              window.open(
                `${baseWebReportUrl}/orders/${orderNumber}/reports?report_id=${reportId}`,
                '_blank',
                'rel=noopener noreferrer',
              );
              return;
            }
            window.open(
              `/reference-data/${orderID}/craftsman/${dataSheet}/${activeID}`,
              '_blank',
              'rel=noopener noreferrer',
            );
          }}
          ariaLabel="open in window"
          iconSize="lg"
          size="sm"
        />
      </div>
    </Flex>
  );
};

export const Tkv2ReferenceViewModule = ({
  isPopout,
  isWrapped = false,
  dataSheet,
  popOutIconPosition = {
    top: '.5rem',
    left: '0',
  },
}: Tkv2ReferenceViewModuleProps) => {
  const activeID = useActiveReferenceBarID();
  const propertyID = getPropertyID(activeID);
  const backupListing = useAppSelector((state) => selectBackupListingDataById(state, propertyID));

  const { listingID, address } = useParsedActiveID();
  const { isLoading, isError, data } = useGetListing(listingID);

  if (isLoading) return <LoadingScreen />;
  if (isError) console.warn('Failed to load listing data');

  const listing = isError || data.status === 'missing' ? undefined : data.listing;

  const componentClassNames = ['reference-view-container'];
  if (isPopout) componentClassNames.push('reference-view-container-popout');
  if (isWrapped) componentClassNames.push('reference-view-container-wrapped');

  return (
    <>
      <IconWrapper
        isPopout={isPopout}
        isWrapped={isWrapped}
        popOutIconPosition={popOutIconPosition}
        dataSheet={dataSheet}
        activeID={activeID}
      />
      <div className={componentClassNames.join(' ')}>
        <Tkv2DataComponent
          dataSheet={dataSheet}
          isPopout={isPopout}
          listingData={(!listing || listing.UnparsedAddress !== backupListing?.UnparsedAddress) ? backupListing : listing}
          referenceViewID={activeID}
          address={address ? addressToString(address) : listing?.UnparsedAddress}
        />
      </div>
    </>
  );
};
